// src/components/resumo/annotations/NoteModal.tsx
// 🎯 Modal para criar/editar notas - Identidade GRAIA

import React, { useState } from 'react';
import { X, Save, Star, BookOpen, HelpCircle, Lightbulb } from 'lucide-react';
import { Note, NoteCategory } from './annotations.types';

interface NoteModalProps {
    note?: Note;
    onSave: (data: Partial<Note>) => void;
    onClose: () => void;
    darkMode?: boolean;
}

const NoteModal: React.FC<NoteModalProps> = ({
    note,
    onSave,
    onClose,
    darkMode = false
}) => {
    const [text, setText] = useState(note?.text || '');
    const [category, setCategory] = useState<NoteCategory>(note?.category || 'importante');
    const [question, setQuestion] = useState(note?.question || '');
    const [answer, setAnswer] = useState(note?.answer || '');
    const [createQuiz, setCreateQuiz] = useState(!!note?.question);

    const categories: { value: NoteCategory; label: string; icon: any; color: string }[] = [
        { value: 'importante', label: 'Importante', icon: Star, color: '#fbbf24' },
        { value: 'revisar', label: 'Revisar', icon: BookOpen, color: '#3b82f6' },
        { value: 'duvida', label: 'Dúvida', icon: HelpCircle, color: '#ef4444' },
        { value: 'conceito', label: 'Conceito', icon: Lightbulb, color: '#10b981' }
    ];

    const handleSave = () => {
        if (!text.trim()) {
            alert('Digite o texto da nota');
            return;
        }

        onSave({
            text,
            category,
            question: createQuiz ? question : undefined,
            answer: createQuiz ? answer : undefined
        });
    };

    return (
        <div className={`note-modal-overlay ${darkMode ? 'dark' : ''}`} onClick={onClose}>
            <div className="note-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="note-modal-header">
                    <h3>{note ? 'Editar Nota' : 'Nova Nota'}</h3>
                    <button className="note-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="note-modal-body">
                    {/* Texto */}
                    <div className="note-modal-field">
                        <label>Texto da Nota</label>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Digite sua nota aqui..."
                            rows={4}
                        />
                    </div>

                    {/* Categoria */}
                    <div className="note-modal-field">
                        <label>Categoria</label>
                        <div className="note-modal-categories">
                            {categories.map(cat => {
                                const Icon = cat.icon;
                                return (
                                    <button
                                        key={cat.value}
                                        className={`note-modal-category-btn ${category === cat.value ? 'active' : ''}`}
                                        style={{
                                            borderColor: category === cat.value ? cat.color : 'transparent'
                                        }}
                                        onClick={() => setCategory(cat.value)}
                                    >
                                        <Icon size={16} style={{ color: cat.color }} />
                                        <span>{cat.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quiz Opcional */}
                    <div className="note-modal-field">
                        <label className="note-modal-checkbox">
                            <input
                                type="checkbox"
                                checked={createQuiz}
                                onChange={(e) => setCreateQuiz(e.target.checked)}
                            />
                            <span>Transformar em pergunta para revisão</span>
                        </label>
                    </div>

                    {createQuiz && (
                        <>
                            <div className="note-modal-field">
                                <label>Pergunta</label>
                                <input
                                    type="text"
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    placeholder="Digite a pergunta..."
                                />
                            </div>

                            <div className="note-modal-field">
                                <label>Resposta</label>
                                <textarea
                                    value={answer}
                                    onChange={(e) => setAnswer(e.target.value)}
                                    placeholder="Digite a resposta..."
                                    rows={3}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="note-modal-footer">
                    <button className="note-modal-btn cancel" onClick={onClose}>
                        Cancelar
                    </button>
                    <button className="note-modal-btn save" onClick={handleSave}>
                        <Save size={16} />
                        <span>Salvar</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NoteModal;